import React, { FC, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import styled from 'styled-components';
import { hexToRgb, T } from '../../../../../shared/helpers';
import { PaletteType } from '../../../../../shared/enums';
import { Icon } from '../../../../widgets/icon';
import AngleUpIcon from 'src/shared/material_icons/hardware/keyboard_arrow_up.svg';
import { spin } from '../../styles/animations';
import { colorPickerView_paletteColorsQuery } from './__generated__/colorPickerView_paletteColorsQuery.graphql';
import { colorPickerView_palettesListQuery } from './__generated__/colorPickerView_palettesListQuery.graphql';
import { colorPickerView_paletteColorsFragment$key } from './__generated__/colorPickerView_paletteColorsFragment.graphql';
import { colorPickerView_palettesListFragment$key } from './__generated__/colorPickerView_palettesListFragment.graphql';

/** Pantone color payload bubbled up when a Pantone tile is clicked. */
export interface PantoneColorPayload {
    id: string;
    rgbHexCode: string;
    spotColorName: string | null;
    name: string | null;
    paletteName: string;
}

interface ColorPickerViewProps {
    initialColor?: string;
    isApplyingRgb?: boolean;
    applyingPantoneId?: string | null;
    onApplyRgb: (hex: string) => void;
    onApplyPantone: (color: PantoneColorPayload) => void;
    onCancel: () => void;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    padding: 4px 6px;
    border-radius: 6px;
    cursor: pointer;
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;

    &:hover:not(:disabled) {
        background: #f3f4f6;
        color: #1f2937;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    ${Icon} {
        width: 12px;
        height: 12px;
        fill: currentColor;
        transform: rotate(-90deg);
    }
`;

const Title = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #6b7280;
    letter-spacing: 0.5px;
`;

const Tabs = styled.div`
    display: flex;
    gap: 4px;
    padding: 3px;
    background: #f3f4f6;
    border-radius: 8px;
`;

const TabButton = styled.button<{ $active?: boolean }>`
    flex: 1;
    padding: 6px 10px;
    border: 0;
    background: ${(props) => (props.$active ? '#fff' : 'transparent')};
    color: ${(props) => (props.$active ? '#7c3aed' : '#6b7280')};
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    box-shadow: ${(props) => (props.$active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none')};

    &:hover:not(:disabled) {
        color: ${(props) => (props.$active ? '#7c3aed' : '#1f2937')};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PickerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    .react-colorful {
        width: 100%;
        height: 180px;
    }

    .react-colorful__saturation {
        border-radius: 8px;
    }

    .react-colorful__hue {
        border-radius: 8px;
        margin-top: 10px;
        height: 10px;
    }

    .react-colorful__pointer {
        width: 18px;
        height: 18px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
`;

const InputsRow = styled.div`
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    gap: 6px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const FieldLabel = styled.label`
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.4px;
`;

const inputStyles = `
	width: 100%;
	padding: 6px 8px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 12px;
	background: #f9fafb;
	text-align: center;
	font-family: sans-serif;
	color: #1f2937;

	&:focus {
		outline: none;
		border-color: #8b5cf6;
		background: #fff;
	}
`;

const HexInput = styled(HexColorInput)`
    ${inputStyles}
    text-align: left;
    text-transform: uppercase;
`;

const RgbInput = styled.input`
    ${inputStyles}
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const ApplyButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 12px;
    border: 0;
    border-radius: 8px;
    background: #8b5cf6;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
        background: #7c3aed;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Spinner = styled.div<{ $light?: boolean }>`
    width: 14px;
    height: 14px;
    border: 2px solid ${(props) => (props.$light ? 'rgba(255, 255, 255, 0.5)' : '#e5e7eb')};
    border-top-color: ${(props) => (props.$light ? '#fff' : '#8b5cf6')};
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12px;
    background: #f9fafb;

    &:focus {
        outline: none;
        border-color: #8b5cf6;
        background: #fff;
    }

    &:disabled {
        opacity: 0.5;
    }
`;

const PantoneScroll = styled.div`
    max-height: 320px;
    overflow-y: auto;
    padding-right: 2px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const PantoneGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
`;

const PantoneItem = styled.button`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    transition: all 0.15s ease;
    min-height: 70px;
    position: relative;
    text-align: left;

    &:hover:not(:disabled) {
        border-color: #8b5cf6;
        background: rgba(139, 92, 246, 0.04);
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

// Dynamic background applied via the `style` prop at the call site to avoid generating a new
// CSS class per color (would otherwise trigger styled-components' >200-class warning and lag
// the UI when many Pantone swatches render).
const Swatch = styled.div`
    width: 100%;
    aspect-ratio: 2.2;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.06);
`;

const PantoneLabel = styled.div`
    font-size: 10px;
    font-weight: 600;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PantoneSubLabel = styled.div`
    font-size: 9px;
    color: #9ca3af;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SpinnerOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 10px;
`;

const EmptyState = styled.div`
    padding: 32px 12px;
    text-align: center;
    font-size: 12px;
    color: #9ca3af;
`;

const LoadingState = styled.div`
    padding: 32px 12px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const clampByte = (raw: string): number => {
    const n = parseInt(raw, 10);
    if (isNaN(n)) return 0;
    return Math.max(0, Math.min(255, n));
};

const RgbTab: FC<{
    initialColor?: string;
    isApplying?: boolean;
    onApply: (hex: string) => void;
}> = ({ initialColor, isApplying, onApply }) => {
    const [hex, setHex] = useState<string>(() => (initialColor && /^#[0-9a-f]{6}$/i.test(initialColor) ? initialColor : '#8b5cf6'));

    const rgb = (() => {
        try {
            return hexToRgb(hex);
        } catch {
            return { r: 0, g: 0, b: 0 };
        }
    })();

    const setRgb = (r: number, g: number, b: number) => {
        const toHex = (v: number) => v.toString(16).padStart(2, '0');
        setHex(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
    };

    return (
        <>
            <PickerContainer>
                <HexColorPicker color={hex} onChange={setHex} />
            </PickerContainer>

            <InputsRow>
                <Field>
                    <FieldLabel>{T._('Hex', 'Admin')}</FieldLabel>
                    <HexInput color={hex} prefixed onChange={setHex} />
                </Field>
                <Field>
                    <FieldLabel>R</FieldLabel>
                    <RgbInput type='number' min={0} max={255} value={rgb.r} onChange={(e) => setRgb(clampByte(e.currentTarget.value), rgb.g, rgb.b)} />
                </Field>
                <Field>
                    <FieldLabel>G</FieldLabel>
                    <RgbInput type='number' min={0} max={255} value={rgb.g} onChange={(e) => setRgb(rgb.r, clampByte(e.currentTarget.value), rgb.b)} />
                </Field>
                <Field>
                    <FieldLabel>B</FieldLabel>
                    <RgbInput type='number' min={0} max={255} value={rgb.b} onChange={(e) => setRgb(rgb.r, rgb.g, clampByte(e.currentTarget.value))} />
                </Field>
            </InputsRow>

            <ApplyButton type='button' onClick={() => onApply(hex)} disabled={isApplying}>
                {isApplying && <Spinner $light />}
                {isApplying ? T._('Applying...', 'Admin') : T._('Apply color', 'Admin')}
            </ApplyButton>
        </>
    );
};

// === Pantone tab: pagination-aware data layer ===

const PAGE_SIZE_PALETTES = 20;
const PAGE_SIZE_COLORS = 50;
const SEARCH_DEBOUNCE_MS = 300;
const PREVIEW_COLORS_PER_PALETTE = 6;

// Paletteslist fragment used for browsing Pantone palettes (compact: 6 preview colors per palette).
const PalettesListFragment = graphql`
    fragment colorPickerView_palettesListFragment on Query
    @refetchable(queryName: "colorPickerView_palettesListPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 20 }, cursor: { type: "String" }, paletteTypeID: { type: "Int" }) {
        palettesWithColors(first: $count, after: $cursor, numberOfColorsToShow: 6, paletteTypeID: $paletteTypeID, orderBy: "DESC")
            @connection(key: "colorPickerView_palettesList_palettesWithColors") {
            edges {
                node {
                    id
                    name
                    colors {
                        colorID
                        name
                        rgbHexCode
                        spotColorName
                    }
                }
            }
        }
    }
`;

const PalettesListQuery = graphql`
    query colorPickerView_palettesListQuery($paletteTypeID: Int) {
        ...colorPickerView_palettesListFragment @arguments(paletteTypeID: $paletteTypeID)
    }
`;

// Palette colors fragment reused for two modes: viewing a single palette's colors (id passed) or
// searching colors globally (search passed). Pagination via @connection + usePaginationFragment.
const PaletteColorsFragment = graphql`
    fragment colorPickerView_paletteColorsFragment on Query
    @refetchable(queryName: "colorPickerView_paletteColorsPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 50 }
        cursor: { type: "String" }
        paletteTypeID: { type: "Int" }
        paletteId: { type: "String" }
        search: { type: "String" }
    ) {
        paletteColors(first: $count, after: $cursor, id: $paletteId, search: $search, paletteTypeID: $paletteTypeID, orderBy: "DESC")
            @connection(key: "colorPickerView_paletteColors_paletteColors") {
            edges {
                node {
                    id
                    colorID
                    name
                    rgbHexCode
                    spotColorName
                }
            }
        }
    }
`;

const PaletteColorsQuery = graphql`
    query colorPickerView_paletteColorsQuery($paletteTypeID: Int, $paletteId: String, $search: String) {
        ...colorPickerView_paletteColorsFragment @arguments(paletteTypeID: $paletteTypeID, paletteId: $paletteId, search: $search)
    }
`;

// === Pantone tab: extra styled components ===

const PalettesScroll = styled.div`
    max-height: 320px;
    overflow-y: auto;
    padding-right: 2px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const PaletteCard = styled.button`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
    width: 100%;

    &:hover:not(:disabled) {
        border-color: #8b5cf6;
        background: rgba(139, 92, 246, 0.04);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const PaletteCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const PaletteCardName = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
`;

const PaletteCardChevron = styled.div`
    color: #9ca3af;
    font-size: 14px;
    flex-shrink: 0;
`;

const PaletteCardSwatches = styled.div`
    display: flex;
    gap: 4px;
`;

const PaletteCardSwatch = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
`;

const LoadMoreButton = styled.button`
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        border-color: #8b5cf6;
        color: #7c3aed;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const DetailHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 2px;
`;

const DetailHeaderTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #1f2937;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// === Pantone tab: small hook to debounce the search input ===

function useDebouncedValue<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
}

// === Pantone tab: palette list sub-view ===

const PalettesListView: FC<{
    onSelectPalette: (paletteId: string, paletteName: string) => void;
    applyingId: string | null | undefined;
}> = ({ onSelectPalette, applyingId }) => {
    const queryRef = useLazyLoadQuery<colorPickerView_palettesListQuery>(PalettesListQuery, { paletteTypeID: PaletteType.Pantone });
    const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(PalettesListFragment, queryRef as colorPickerView_palettesListFragment$key);

    const palettes = useMemo(() => {
        const edges = data.palettesWithColors?.edges ?? [];
        return edges
            .map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => !!node)
            .map((node) => ({
                id: node.id,
                name: node.name ?? '',
                colors: (node.colors ?? []).filter((c): c is NonNullable<typeof c> => !!c).slice(0, PREVIEW_COLORS_PER_PALETTE)
            }));
    }, [data.palettesWithColors]);

    if (palettes.length === 0) {
        return <EmptyState>{T._('No Pantone palettes configured. Create one from Settings → Color palette settings.', 'Admin')}</EmptyState>;
    }

    return (
        <>
            <PalettesScroll>
                {palettes.map((palette) => (
                    <PaletteCard key={palette.id} type='button' onClick={() => onSelectPalette(palette.id, palette.name)} disabled={!!applyingId}>
                        <PaletteCardHeader>
                            <PaletteCardName>{palette.name || T._('Untitled palette', 'Admin')}</PaletteCardName>
                            <PaletteCardChevron>›</PaletteCardChevron>
                        </PaletteCardHeader>
                        <PaletteCardSwatches>
                            {palette.colors.map((c) => (
                                <PaletteCardSwatch key={c.colorID} style={{ background: c.rgbHexCode || '#000000' }} />
                            ))}
                        </PaletteCardSwatches>
                    </PaletteCard>
                ))}
            </PalettesScroll>
            {hasNext && (
                <LoadMoreButton type='button' onClick={() => loadNext(PAGE_SIZE_PALETTES)} disabled={isLoadingNext || !!applyingId}>
                    {isLoadingNext ? T._('Loading...', 'Admin') : T._('Load more palettes', 'Admin')}
                </LoadMoreButton>
            )}
        </>
    );
};

// === Pantone tab: palette colors sub-view (used for both detail mode and global search) ===

const PaletteColorsView: FC<{
    paletteId?: string;
    paletteName?: string;
    search?: string;
    onSelect: (color: PantoneColorPayload) => void;
    applyingId: string | null | undefined;
}> = ({ paletteId, paletteName, search, onSelect, applyingId }) => {
    const queryRef = useLazyLoadQuery<colorPickerView_paletteColorsQuery>(PaletteColorsQuery, {
        paletteTypeID: PaletteType.Pantone,
        paletteId: paletteId ?? null,
        search: search ?? null
    });
    const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(PaletteColorsFragment, queryRef as colorPickerView_paletteColorsFragment$key);

    const colors = useMemo(() => {
        const edges = data.paletteColors?.edges ?? [];
        return edges.map((edge) => edge?.node).filter((node): node is NonNullable<typeof node> => !!node);
    }, [data.paletteColors]);

    if (colors.length === 0) {
        const message = search ? T._('No Pantone colors match your search.', 'Admin') : T._('This palette has no colors.', 'Admin');
        return <EmptyState>{message}</EmptyState>;
    }

    return (
        <>
            <PantoneScroll>
                <PantoneGrid>
                    {colors.map((color) => {
                        // Use palette+color composite key in detail mode (same color id can appear across palettes);
                        // search-mode results don't have palette context here, so fall back to color id alone.
                        const itemKey = paletteId ? `${paletteId}:${color.colorID}` : `${color.id}`;
                        const isApplying = applyingId === itemKey;
                        return (
                            <PantoneItem
                                key={itemKey}
                                type='button'
                                onClick={() =>
                                    onSelect({
                                        id: itemKey,
                                        rgbHexCode: color.rgbHexCode || '#000000',
                                        spotColorName: color.spotColorName ?? null,
                                        name: color.name ?? null,
                                        paletteName: paletteName ?? ''
                                    })
                                }
                                disabled={!!applyingId}
                            >
                                <Swatch style={{ background: color.rgbHexCode || '#000000' }} />
                                <PantoneLabel>{color.spotColorName || color.name || color.rgbHexCode}</PantoneLabel>
                                {color.name && color.spotColorName && <PantoneSubLabel>{color.name}</PantoneSubLabel>}
                                {isApplying && (
                                    <SpinnerOverlay>
                                        <Spinner />
                                    </SpinnerOverlay>
                                )}
                            </PantoneItem>
                        );
                    })}
                </PantoneGrid>
            </PantoneScroll>
            {hasNext && (
                <LoadMoreButton type='button' onClick={() => loadNext(PAGE_SIZE_COLORS)} disabled={isLoadingNext || !!applyingId}>
                    {isLoadingNext ? T._('Loading...', 'Admin') : T._('Load more colors', 'Admin')}
                </LoadMoreButton>
            )}
        </>
    );
};

// === Pantone tab: state machine ===

type PantoneView = { kind: 'list' } | { kind: 'detail'; paletteId: string; paletteName: string };

const PantoneTabContent: FC<{
    applyingId: string | null | undefined;
    onSelect: (color: PantoneColorPayload) => void;
}> = ({ applyingId, onSelect }) => {
    const [view, setView] = useState<PantoneView>({ kind: 'list' });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

    const handleSelectPalette = useCallback((paletteId: string, paletteName: string) => {
        setView({ kind: 'detail', paletteId, paletteName });
        setSearch('');
    }, []);

    const handleBack = useCallback(() => {
        setView({ kind: 'list' });
        setSearch('');
    }, []);

    const inDetail = view.kind === 'detail';
    const placeholder = inDetail ? `${T._('Search in', 'Admin')} ${view.paletteName}...` : T._('Search Pantone color or palette...', 'Admin');

    // Determine which sub-view to render and a stable key for it. The key change forces a clean
    // re-mount when transitioning between modes (list ↔ search) or between palettes, so each sub-view
    // starts with a fresh paginated connection rather than carrying stale data over.
    let body: React.ReactNode;
    let bodyKey: string;
    if (inDetail) {
        bodyKey = `detail:${view.paletteId}:${debouncedSearch}`;
        body = (
            <PaletteColorsView
                paletteId={view.paletteId}
                paletteName={view.paletteName}
                search={debouncedSearch || undefined}
                onSelect={onSelect}
                applyingId={applyingId}
            />
        );
    } else if (debouncedSearch) {
        bodyKey = `search:${debouncedSearch}`;
        body = <PaletteColorsView search={debouncedSearch} onSelect={onSelect} applyingId={applyingId} />;
    } else {
        bodyKey = 'list';
        body = <PalettesListView onSelectPalette={handleSelectPalette} applyingId={applyingId} />;
    }

    return (
        <>
            {inDetail && (
                <DetailHeader>
                    <BackButton type='button' onClick={handleBack} disabled={!!applyingId}>
                        <Icon>
                            <AngleUpIcon />
                        </Icon>
                        {T._('Back', 'Admin')}
                    </BackButton>
                    <DetailHeaderTitle>{view.paletteName || T._('Untitled palette', 'Admin')}</DetailHeaderTitle>
                </DetailHeader>
            )}

            <SearchInput type='text' placeholder={placeholder} value={search} onChange={(e) => setSearch(e.currentTarget.value)} disabled={!!applyingId} />

            <Suspense
                fallback={
                    <LoadingState>
                        <Spinner />
                    </LoadingState>
                }
            >
                <React.Fragment key={bodyKey}>{body}</React.Fragment>
            </Suspense>
        </>
    );
};

const ColorPickerPanel: FC<ColorPickerViewProps> = ({ initialColor, isApplyingRgb, applyingPantoneId, onApplyRgb, onApplyPantone, onCancel }) => {
    const [activeTab, setActiveTab] = useState<'rgb' | 'pantone'>('rgb');
    const busy = !!isApplyingRgb || !!applyingPantoneId;

    return (
        <Container>
            <Header>
                <BackButton type='button' onClick={onCancel} disabled={busy}>
                    <Icon>
                        <AngleUpIcon />
                    </Icon>
                    {T._('Back', 'Admin')}
                </BackButton>
                <Title>{T._('Pick a color', 'Admin')}</Title>
            </Header>

            <Tabs>
                <TabButton type='button' $active={activeTab === 'rgb'} onClick={() => setActiveTab('rgb')} disabled={busy}>
                    {T._('RGB', 'Admin')}
                </TabButton>
                <TabButton type='button' $active={activeTab === 'pantone'} onClick={() => setActiveTab('pantone')} disabled={busy}>
                    {T._('Pantone', 'Admin')}
                </TabButton>
            </Tabs>

            {activeTab === 'rgb' && <RgbTab initialColor={initialColor} isApplying={isApplyingRgb} onApply={onApplyRgb} />}
            {activeTab === 'pantone' && <PantoneTabContent applyingId={applyingPantoneId} onSelect={onApplyPantone} />}
        </Container>
    );
};

export const ColorPickerView: FC<ColorPickerViewProps> = (props) => <ColorPickerPanel {...props} />;

export default ColorPickerView;
